import { useMemo } from 'react';
import uniq from 'lodash/uniq';
import useAsync from 'react-use/lib/useAsync';
import { httpClient } from '@alfa-bank/site-http-client';

import type { ICitiesByUuidResponse, IRawCity } from '../model/http';
import type { TTopCities } from '../../models';
import { MAX_TOP_CITIES, UNKNOWN_FIAS_ID } from '../../config';
import { citiesEndPoint } from '../config';

export function useTopCities({
    autoDetectedFiasId,
    currentCity,
    topCities,
}: {
    autoDetectedFiasId: string | null;
    currentCity: IRawCity | null;
    topCities: TTopCities;
}) {
    const citiesToFetch = useMemo(() => {
        // Если город по IP или выбранный город еще грузятся - города запрашивать не надо
        if (!autoDetectedFiasId || !currentCity || !topCities) return null;

        const slicedTopCities = topCities.slice(0, MAX_TOP_CITIES);
        // Если город по IP отличается от выбранного города, добавляем его в начало списка
        if (autoDetectedFiasId !== UNKNOWN_FIAS_ID && autoDetectedFiasId !== currentCity?.fiasId) {
            return uniq([autoDetectedFiasId, ...slicedTopCities]);
        }

        // Если список топ-городов содержит город по IP, перемещаем его на первое место
        if (slicedTopCities.includes(autoDetectedFiasId)) {
            slicedTopCities.sort(id => (id === autoDetectedFiasId ? -1 : 0));
        }

        return slicedTopCities;
    }, [autoDetectedFiasId, currentCity, topCities]);

    const response = useAsync(async () => {
        if (citiesToFetch === null) return [];

        const { data } = await httpClient.get<ICitiesByUuidResponse>(citiesEndPoint, {
            'fiasId.in': citiesToFetch.join(','),
        });

        /**
         * Если в списке топ городов будет ошибка или бекенд просто не найдет такой город.
         * То мы на всякий случай отфильтруем его от undefined после .find()
         */
        return citiesToFetch
            .map(id => data.data.find(city => city.fiasId === id))
            .filter(Boolean) as IRawCity[];
    }, [citiesToFetch]);

    return response.value ?? [];
}
